import { useRequiredContext } from "@redotech/react-util/context";
import { useTranslation } from "@redotech/redo-i18n";
import FileCheck02Icon from "@redotech/redo-icons/file-check-02.svg";
import FileCheck02FilledIcon from "@redotech/redo-icons/file-check-02_filled.svg";
import Settings02Icon from "@redotech/redo-icons/settings-02.svg";
import Settings02FilledIcon from "@redotech/redo-icons/settings-02_filled.svg";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import type { SettingsVisibility } from "@redotech/redo-merchant-app-common/settings/use-settings-visibility";
import {
  SettingsSection,
  SettingsSectionEnum,
} from "@redotech/redo-web/arbiter-components/navigation/redo-settings-nav";
import { getSettingsTabHref } from "@redotech/redo-web/settings-search/settings-search";
import {
  getOmsSettingsTabLabel,
  OMS_SETTINGS_SECTION_QUERY,
  omsSettingsTabs,
} from "../../../setting/order-management/order-management-settings-tabs";

import { navPage, settingsSection } from "./settings-section";
import { useSharedSettingsItems } from "./shared-settings-items";

export function useFulfillmentSettings(
  visibility: SettingsVisibility,
): SettingsSection | undefined {
  const shared = useSharedSettingsItems();
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  return settingsSection(
    visibility,
    "order-fulfillment",
    SettingsSectionEnum.ORDER_FULFILLMENT,
    [
      navPage("order-fulfillment/order-management", {
        id: "order-management",
        href: routes.getSettingsSubRoute("order-management"),
        friendlyName: t("Order management"),
        searchTabs: omsSettingsTabs.map((tab) => ({
          href: getSettingsTabHref({
            href: routes.getSettingsSubRoute("order-management"),
            param: OMS_SETTINGS_SECTION_QUERY,
            tab,
          }),
          id: tab,
          label: getOmsSettingsTabLabel(tab),
        })),
        searchTerms: [
          "order preparation",
          "order intake",
          "addresses",
          "address validation",
          "address exceptions and outreach",
          "order eligibility",
          "pickup and carrier handoff",
          "carrier handoff",
          "USPS manifest",
          "protected fulfillment actions",
          "order lookup",
          "parcel verification photos",
          "fulfillment defaults",
          "fulfillment locations",
          "international shipping",
          "international shipments",
          "custom fulfillment data",
          "merge suggestions",
          "auto-merge criteria",
          "merged shipment outcomes",
          "merge exceptions",
          "bulk shipment suggestions",
          "automated learning",
          "auto-split orders",
          "auto-split conditions",
          "when to split",
          "how to split",
          "after splitting",
          "split exceptions",
          "product sku mappings",
          "fulfillment promise",
          "fulfillment cutoff",
          "fulfillment schedule",
          "use a custom schedule",
          "working days and cutoffs",
          "timezone",
          "print profiles",
          "documents",
          "pick list",
          "printers and workstations",
          "fulfillment actions",
          "default action button",
          "bulk shipment matching",
          "one click print",
          "presets and shortcuts",
          "shipping presets",
          "keyboard shortcuts",
          "hotkeys",
          "packages",
          "parcels",
          "package dimensions",
          "package weight",
          "custom imports",
          "CSV column mappings",
          "import templates",
        ],
        UnfilledIcon: Settings02Icon,
        FilledIcon: Settings02FilledIcon,
      }),
      navPage(
        "order-fulfillment/order-editing",
        shared["order-fulfillment/order-editing"],
      ),
      navPage(
        "order-fulfillment/automations",
        shared["order-fulfillment/automations"],
      ),
      navPage("order-fulfillment/packing-slips", {
        id: "packing-slips",
        href: routes.getSettingsSubRoute("oms/printing/packing-slips"),
        friendlyName: t("Packing slips"),
        UnfilledIcon: FileCheck02Icon,
        FilledIcon: FileCheck02FilledIcon,
      }),
      navPage("order-fulfillment/reports", shared["order-fulfillment/reports"]),
      navPage(
        "order-fulfillment/scanning-warehouse",
        shared["order-fulfillment/scanning-warehouse"],
      ),
      navPage(
        "order-fulfillment/warehouse-dashboard",
        shared["order-fulfillment/warehouse-dashboard"],
      ),
      navPage(
        "order-fulfillment/warehouse-layouts",
        shared["order-fulfillment/warehouse-layouts"],
      ),
      navPage(
        "order-fulfillment/work-order-flows",
        shared["order-fulfillment/work-order-flows"],
      ),
    ],
  );
}

export function withNewGeneralSearchTerms(
  section: SettingsSection | undefined,
): SettingsSection | undefined {
  if (!section) {
    return section;
  }
  const terms: Record<string, string[]> = {
    "billing-activity-button": [
      "billing activity",
      "billing reports",
      "transactions",
    ],
    "bills-button": [
      "bill summary",
      "billing summary",
      "bill totals",
      "invoices",
      "date range",
      "payment methods",
    ],
  };
  return {
    ...section,
    items: section.items.map((item) =>
      item.children
        ? {
            ...item,
            children: item.children.map((child) =>
              terms[child.id]
                ? { ...child, searchTerms: terms[child.id] }
                : child,
            ),
          }
        : item,
    ),
  };
}

export function useShippingSettings(
  visibility: SettingsVisibility,
): SettingsSection | undefined {
  const shared = useSharedSettingsItems();
  return settingsSection(visibility, "shipping", SettingsSectionEnum.SHIPPING, [
    navPage("shipping/carriers", shared["shipping/carriers"]),
    navPage("shipping/freight", shared["shipping/freight"]),
    navPage("shipping/discounts", shared["shipping/discounts"]),
    navPage("shipping/cross-border", shared["shipping/cross-border"]),
  ]);
}
